<script setup>
import { ref, watch, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  document: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close', 'success', 'update:document']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const documentName = ref('');
const isSubmitting = ref(false);

// 表单错误
const errorMessage = ref('');

// 监听 document 变化，初始化表单
watch(
  () => props.document,
  newDoc => {
    if (newDoc) {
      documentName.value = newDoc.name || '';
    }
  },
  { immediate: true }
);

// 打开对话框
const open = () => {
  dialogRef.value?.open();
};

// 验证表单
const validateForm = () => {
  if (!documentName.value || documentName.value.trim() === '') {
    errorMessage.value = t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FORM_NAME_ERROR');
    return false;
  }
  errorMessage.value = '';
  return true;
};

// 提交编辑
const handleSubmit = async () => {
  if (!validateForm()) {
    return;
  }

  isSubmitting.value = true;

  try {
    const updatedDoc = await store.dispatch('pilotDocuments/update', {
      id: props.document.id,
      name: documentName.value.trim(),
    });

    useAlert(t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.EDIT_SUCCESS'));
    emit('success', updatedDoc);
    emit('update:document', updatedDoc);
    dialogRef.value?.close();
  } catch (error) {
    const errorMsg =
      error.message || t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.EDIT_ERROR');
    useAlert(errorMsg);
  } finally {
    isSubmitting.value = false;
  }
};

// 取消
const handleCancel = () => {
  dialogRef.value?.close();
};

defineExpose({ dialogRef, open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="alert"
    :title="t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.EDIT_TITLE')"
    :show-cancel-button="true"
    :cancel-button-label="t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FORM.CANCEL')"
    :confirm-button-label="t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FORM.UPDATE')"
    :confirm-button-loading="isSubmitting"
    @confirm="handleSubmit"
    @cancel="handleCancel"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="documentName"
        :label="t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FORM_NAME_LABEL')"
        :placeholder="
          t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.FORM_NAME_PLACEHOLDER')
        "
        :message="errorMessage"
        :message-type="errorMessage ? 'error' : 'info'"
      />
    </div>
  </Dialog>
</template>
